import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useCampaignPeople, type PeopleFilter } from '../hooks/useCampaignPeople';
import { useBlockActions } from '../hooks/useBlockActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import type { PeopleRow as PeopleRowData } from '../outreachTypes';
import { PeopleFilters } from './PeopleFilters';
import { PeopleRow } from './PeopleRow';
import { PitchDetailsDrawer } from './details/PitchDetailsDrawer';
import { peopleDayCounts, peopleDayLabel } from './peopleDay';
import { peopleMessages as messages } from './peopleMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import campaign from './Campaign.module.scss';
import styles from './people/People.module.scss';
/* eslint-enable css-modules/no-unused-class */

const NO_FILTER: PeopleFilter = { kind: null, result: null };

interface PeopleListProps {
  accountId: number;
  campaignUuid: string;
  /** `YYYY-MM-DD` of the release, for the "release day" line; null when unknown. */
  releaseDay: string | null;
}

/**
 * The People tab (design outreach-v2 t1): everyone Plug picked, planned first, then by send day,
 * newest first. A click on a row opens its details (t2-t4); the arrows of the panel step through
 * the rows. `outreach.message.updated`, `outreach.reply.received` and `outreach.placement.found`
 * read it again (`useOutreachRealtime`).
 */
const PeopleListComponent: React.FC<PeopleListProps> = ({ accountId, campaignUuid, releaseDay }) => {
  const intl = useIntl();
  const { isDemo } = useOutreachPlan();
  const [filter, setFilter] = useState<PeopleFilter>(NO_FILTER);
  const [openedUuid, setOpenedUuid] = useState<string | null>(null);
  const people = useCampaignPeople(accountId, campaignUuid, filter);
  const actions = useBlockActions(accountId, campaignUuid);
  const close = useCallback(() => setOpenedUuid(null), []);
  const open = useCallback((row: PeopleRowData) => setOpenedUuid(row.messageUuid), []);
  const more = useCallback(() => {
    people.loadMore().catch(console.error);
  }, [people]);
  const rows = useMemo(() => people.days.flatMap((day) => day.rows), [people.days]);
  // The panel reads the row of the newest list: a realtime read updates it, a blocked row closes it.
  const opened = useMemo(() => rows.find((row) => row.messageUuid === openedUuid) ?? null, [openedUuid, rows]);

  const groups = useMemo(
    () =>
      people.days.map((day) => (
        <React.Fragment key={day.day}>
          <div className={clsx(styles.day, day.sent === 0 && styles.dayPlanned)} data-testid="people-day">
            {day.sent === 0
              ? intl.formatMessage(messages.dayPlanned, { day: peopleDayLabel(intl, day.day, releaseDay) })
              : peopleDayLabel(intl, day.day, releaseDay)}
            <span className={styles.dayCount}>{peopleDayCounts(intl, day)}</span>
          </div>
          {day.rows.map((row) => (
            <PeopleRow key={row.messageUuid} row={row} open={openedUuid === row.messageUuid} onOpen={open} />
          ))}
        </React.Fragment>
      )),
    [intl, open, openedUuid, people.days, releaseDay]
  );

  const body = () => {
    if (people.isLoading) {
      return <LoadingView />;
    }
    if (people.error !== null) {
      return <ErrorView error={people.error} retry={people.retry} />;
    }
    if (people.days.length === 0) {
      const isFiltered = filter.kind !== null || filter.result !== null;
      return (
        <p className={styles.empty} data-testid="people-empty">
          {intl.formatMessage(isFiltered ? messages.emptyFiltered : messages.empty)}
        </p>
      );
    }
    return (
      <>
        <div className={styles.cols} aria-hidden>
          <span />
          <span>{intl.formatMessage(messages.colWho)}</span>
          <span>{intl.formatMessage(messages.colWhy)}</span>
          <span>{intl.formatMessage(messages.colStatus)}</span>
          <span />
        </div>
        {groups}
        {people.hasMore && (
          <div className={styles.foot}>
            <SMLButton size="sm" outline omitCap onClick={more} isLoading={people.isLoadingMore} testID="people-more">
              {intl.formatMessage(messages.showMore)}
            </SMLButton>
          </div>
        )}
      </>
    );
  };

  return (
    <section className={`${campaign.card} ${campaign.cardFlush}`} data-testid="people-list">
      <div className={styles.head}>
        <div>
          <h3 className={campaign.cardTitle}>{intl.formatMessage(messages.title)}</h3>
          <p className={campaign.cardIntro}>{intl.formatMessage(messages.intro)}</p>
        </div>
        <PeopleFilters filter={filter} onChange={setFilter} />
      </div>
      {body()}
      <PitchDetailsDrawer
        accountId={accountId}
        campaignUuid={campaignUuid}
        rows={rows}
        row={opened}
        actions={actions}
        disabled={isDemo}
        onSelect={open}
        onClose={close}
      />
    </section>
  );
};

export const PeopleList = TypedMemo(PeopleListComponent);
